import React, { useState, useCallback, useContext, createContext, ReactNode, useEffect } from 'react';
import { isSystemAdmin as isSystemAdminCheck } from '@turn-connect/hooks';
import { adminService } from '../services/auth';
import { useAccountSummaries } from '../queries/adminReference';

export interface Account {
  id: string;
  name: string;
  role?: string;
}

export interface AccountContextType {
  selectedAccountId: string | null;
  availableAccounts: Account[];
  selectAccount: (accountId: string) => void | Promise<void>;
  refreshAccounts: () => Promise<void>;
  isLoading: boolean;
  error?: string;
  isSystemAdmin: boolean;
}

const NO_PERMISSIONS: string[] = [];

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const useAccountContext = (): AccountContextType => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccountContext must be used within an AccountProvider');
  }
  return context;
};

export const AccountProvider: React.FC<{
  children: ReactNode;
  initialUser?: any;
  initialPermissions?: string[];
  isAuthenticated?: boolean;
  isAuthLoading?: boolean;
}> = ({ children, initialUser, initialPermissions, isAuthenticated = false, isAuthLoading = false }) => {
  // Use props for authentication state to avoid creating a separate useAuth instance
  // This prevents desynchronization between App.tsx's auth state and AccountProvider's auth state
  const authLoading = isAuthLoading;

  // Use props for actual user data - they are always provided from App.tsx
  const user = initialUser;
  const permissions = initialPermissions ?? NO_PERMISSIONS;
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [availableAccounts, setAvailableAccounts] = useState<Account[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Check if user is system admin with safe permissions check
  const isSystemAdmin = !!(
    isSystemAdminCheck(permissions) ||
    (user?.email && user.email.includes('sysadmin')) ||
    (user?.email && user.email.includes('admin@'))
  );

  // System admins: every live account as id + name from the shared
  // /accounts/summaries query — the same cache entry the header's account
  // selector reads, so this costs no request of its own. It used to walk the
  // full GET /accounts pages (every column of every account) on each load,
  // and again whenever the permissions array changed identity (every session
  // re-scope), only to keep id and name.
  const summariesQuery = useAccountSummaries({ enabled: !authLoading && isAuthenticated && !!user && isSystemAdmin });
  const summaries = summariesQuery.data;
  const summariesError = summariesQuery.error;

  // Load accounts based on user type
  useEffect(() => {
    // Wait for auth to settle, as the old load effect did.
    if (user === undefined || authLoading) return;
    if (!isAuthenticated || !user) {
      setAvailableAccounts([]);
      return;
    }

    if (isSystemAdmin) {
      const fallbackToOwnAccount = () => {
        if (user.account_id) {
          setAvailableAccounts([
            {
              id: user.account_id,
              name: 'Primary Account (System Admin)',
              role: user.role || 'admin',
            },
          ]);
        }
      };
      if (summariesError) {
        console.warn('Failed to load all accounts, falling back to user account:', summariesError);
        fallbackToOwnAccount();
        return;
      }
      if (!summaries) return; // still loading
      if (summaries.length > 0) {
        setAvailableAccounts(summaries.map((account) => ({ id: account.id, name: account.name, role: 'system.admin' })));
      } else {
        fallbackToOwnAccount();
      }
      return;
    }

    // Regular user - use accounts from user data or primary account
    if (user.accounts && user.accounts.length > 0) {
      setAvailableAccounts(user.accounts);
    } else if (user.account_id) {
      setAvailableAccounts([
        {
          id: user.account_id,
          name: 'Primary Account',
          role: user.role || 'user',
        },
      ]);
    } else {
      setAvailableAccounts([]);
    }
  }, [user, isSystemAdmin, isAuthenticated, authLoading, summaries, summariesError]);

  const isLoading = (isSystemAdmin && summariesQuery.isLoading) || refreshing;

  // Auto-select account if not already selected
  useEffect(() => {
    if (!selectedAccountId && availableAccounts.length > 0) {
      // If user has a primary account_id, use that, otherwise use first available
      const defaultAccount = user?.account_id
        ? availableAccounts.find((acc) => acc.id === user.account_id) || availableAccounts[0]
        : availableAccounts[0];

      if (defaultAccount) {
        setSelectedAccountId(defaultAccount.id);
      }
    }
  }, [selectedAccountId, availableAccounts, user?.account_id]);

  const selectAccount = useCallback(
    async (accountId: string) => {
      setSelectedAccountId(accountId);
      setError(undefined);

      // For multi-account users (not system admins), call backend to re-scope the JWT cookie
      if (!isSystemAdmin && availableAccounts.length > 1) {
        try {
          await adminService.switchAccount(accountId);
        } catch (err) {
          console.warn('Failed to switch account context on backend:', err);
        }
      }
    },
    [isSystemAdmin, availableAccounts]
  );

  const refreshSummaries = summariesQuery.refetch;
  const refreshAccounts = useCallback(async () => {
    // Don't refresh if not authenticated
    if (!isAuthenticated || !user) return;

    setRefreshing(true);
    setError(undefined);

    try {
      if (isSystemAdmin) {
        // The effect above republishes the refreshed summaries.
        await refreshSummaries();
      } else {
        // Regular user - refresh accounts from API
        try {
          const myAccounts = await adminService.getMyAccounts();
          if (myAccounts.length > 0) {
            setAvailableAccounts(
              myAccounts.map((account: any) => ({
                id: account.id,
                name: account.name,
                role: 'user',
              }))
            );
          }
        } catch {
          // Fall back to existing accounts
        }
      }
    } catch (err) {
      console.error('Failed to refresh accounts:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh accounts');
    } finally {
      setRefreshing(false);
    }
  }, [user, isSystemAdmin, isAuthenticated, refreshSummaries]);

  const contextValue: AccountContextType = {
    selectedAccountId,
    availableAccounts,
    selectAccount,
    refreshAccounts,
    isLoading,
    error,
    isSystemAdmin,
  };

  return <AccountContext.Provider value={contextValue}>{children}</AccountContext.Provider>;
};

export { AccountContext };
